import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { Grid, Typography, FormControl } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React, { useState, useEffect, Fragment } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { debounce } from "lodash";

import {
  Autocomplete,
  LoadingButton,
  MandatoryNote,
  SelectField,
  TextField,
} from "../../../../components";

import { useAxios, useAlert } from "../../../../hooks";

// ---- styled TextField ----
const TextFieldWithCSS = styled(TextField)`
  .MuiOutlinedInput-notchedOutline {
    border: 1px solid rgb(0 0 0 / 37%);
    border-radius: 12.5px;
  }
  .MuiOutlinedInput-root {
    font-size: 15px;
  }
  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(0, 0, 0, 0.1);
  }
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(0, 0, 0, 0.1);
  }
  .MuiInputLabel-root {
    color: rgba(0, 0, 0, 0.88);
  }
  .Mui-disabled {
    -webkit-text-fill-color: rgba(0, 0, 0, 0.7);
    background-color: #f5f5f5;
    font-size: 15px;
    border-radius: 12.5px;
  }
  .Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: #f5f5f5;
  }
  .MuiInputLabel-asterisk {
    color: ${(props) => (Boolean(props.required && !props.value) ? "red" : "")};
  }
`;

// ---- Combined Form ----
function AppointmentFormFields({
  formikProps,
  premises,
  premisesID,
  setCustomFields,
  setStepperLevel,
  setDepartments,
}) {
  const axios = useAxios();
  const { alert } = useAlert();
  const dispatch = useDispatch();

  const [officerOptions, setOfficerOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [timeOptions, setTimeOptions] = useState([]);
  const [disabledDate, setDisabledDate] = useState([]);

  // ---- Officer Change ----
  const handleOfficerChange = (value, formikProps) => {
    const selectedOfficer = value ? value.id : "";

    if (selectedOfficer) {
      const officerData = officerOptions.find((o) => o._id === selectedOfficer);
      if (!officerData) return;

      axios({
        url: `admin/custom-fields/get-appointment-fields?business_id=${officerData.business_id}`,
        disableError: true,
        disableRedirect: true,
      }).then((response) => {
        if (response.status) {
          setCustomFields(response.data);
        }
      });

      formikProps.setFieldValue("officer", selectedOfficer);
      formikProps.setFieldValue("department", officerData.department_name);
      formikProps.setFieldValue("departmentID", officerData.department_id);
      formikProps.setFieldValue(
        "subDepartment",
        officerData.sub_department_name
      );
    }
  };

  // ---- Premises Change ----
  const handlePremisesChange = async (event, formikProps) => {
    const selectedPremises = event.target.value;
    formikProps.setFieldValue("premises", selectedPremises);

    try {
      const response = await axios({
        url: `/appointment/appointment/get-departments?premises=${selectedPremises}`,
        method: "GET",
      });
      if (response.status) {
        setDepartments(response.data);
      }
    } catch (error) {
      console.error("Error fetching departments", error);
    }
  };

  useEffect(() => {
    if (premisesID) {
      axios({
        url: `/appointment/appointment/get-premises-officers?premises_id=${premisesID}`,
        disableError: true,
        disableRedirect: true,
      }).then((response) => {
        if (response.status) {
          setOfficerOptions(response.data);
          const officerData = response.data[0];
          if (!officerData) return;

          axios({
            url: `admin/custom-fields/get-appointment-fields?business_id=${officerData.business_id}`,
            disableError: true,
            disableRedirect: true,
          }).then((response) => {
            if (response.status) {
              setCustomFields(response.data);
            }
          });

          formikProps.setFieldValue("officer", response.data[0]?._id);
          formikProps.setFieldValue("department", officerData.department_name);
          formikProps.setFieldValue("departmentID", officerData.department_id);
          formikProps.setFieldValue(
            "subDepartment",
            officerData.sub_department_name
          );
        }
      });
    }
  }, [premisesID]);

  // ---- Fetch Officers ----
  const fetchOfficers = async (premisesId, searchKey = "") => {
    if (!premisesId) return;
    axios({
      url: `/appointment/appointment/get-premises-officers?premises_id=${premisesId}&search=${searchKey}`,
      disableError: true,
      disableRedirect: true,
    }).then((response) => {
      if (response?.status) setOfficerOptions(response.data);
    });
  };

  const debouncedFetchOfficers = debounce((premisesId, searchKey) => {
    fetchOfficers(premisesId, searchKey);
  }, 500);

  // ---- Fetch Time Slots ----
  const fetchDates = async (
    departmentId,
    officerId,
    newValue,
    setTimeOptions
  ) => {
    try {
      const response = await axios({
        url: `/appointment/appointment/get-time-slots?department_id=${departmentId}&date=${newValue}&officer_id=${officerId}`,
        method: "GET",
      });
      if (response.status && response.data.length > 0) {
        setTimeOptions(response.data);
        premisesID &&
          formikProps.setFieldValue("time", [response.data[0]?._id]);
      } else {
        dispatch(
          alert({ type: "error", message: "Booking dates not available" })
        );
      }
    } catch (error) {
      console.error("Error fetching dates", error);
    }
  };

  useEffect(() => {
    if (
      formikProps.values.officer &&
      formikProps.values.departmentID &&
      formikProps.values.date
    ) {
      formikProps.setFieldValue("time", []);
      setTimeOptions([]);

      const time = new Date(formikProps.values.date);

      fetchDates(
        formikProps.values.departmentID,
        formikProps.values.officer,
        time.getTime() / 1000,
        setTimeOptions
      );
    }
  }, [
    premisesID,
    formikProps.values.date,
    formikProps.values.departmentID,
    formikProps.values.officer,
  ]);

  return (
    <Fragment>
      <Grid container spacing={2} sx={{ marginTop: "17.5px" }}>
        {/* Premises */}
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
            Select Premises <span style={{ color: "red" }}>*</span>
          </Typography>
          <SelectField
            placeHolder="Select Premises"
            name="premises"
            value={formikProps.values.premises}
            options={premises.map((p) => ({ id: p._id, name: p.name }))}
            onChange={(e) => handlePremisesChange(e, formikProps)}
            onBlur={formikProps.handleBlur}
            error={
              formikProps.touched.premises &&
              Boolean(formikProps.errors.premises)
            }
            xs={12}
            disabled={premisesID}
          />
        </Grid>

        {/* Officer */}
        {formikProps.values.premises && (
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
              Select Officer <span style={{ color: "red" }}>*</span>
            </Typography>

            <Autocomplete
              name="officer"
              formikProps={formikProps}
              placeholder="Select Officer"
              defaults={{
                primaryKey: "id",
                displayLabel: "name",
              }}
              // value={
              //   formikProps.values.officer
              //     ? officerOptions.find(
              //         (o) => o._id === formikProps.values.officer
              //       ) || null
              //     : null
              // }
              onInputChange={(_, value, reason) => {
                if (reason === "clear" || reason === "reset") {
                  setSearchInput("");
                  formikProps.setFieldValue("officer", "");
                  fetchOfficers(formikProps.values.premises, "");
                } else {
                  setSearchInput(value);
                  debouncedFetchOfficers(formikProps.values.premises, value);
                }
              }}
              options={officerOptions.map((o) => ({
                id: o._id,
                name: o.name,
              }))}
              onChange={(_, value) => {
                handleOfficerChange(value, formikProps);
              }}
              onOpen={() => {
                if (officerOptions.length === 0)
                  fetchOfficers(formikProps.values.premises);
              }}
              error={
                formikProps.touched.officer &&
                Boolean(formikProps.errors.officer)
              }
            />
          </Grid>
        )}

        {/* Department + Sub-department */}
        {formikProps.values.officer && (
          <>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
                Department <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField disabled value={formikProps.values.department} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
                Sub-Department <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField disabled value={formikProps.values.subDepartment} />
            </Grid>
          </>
        )}

        {/* Date Picker */}
        {formikProps.values.subDepartment && (
          <>
            {!premisesID && (
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
                  When are you visiting? <span style={{ color: "red" }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <FormControl fullWidth>
                    <DatePicker
                      value={formikProps.values.date}
                      onChange={(newValue) => {
                        formikProps.setFieldValue("time", []);
                        setTimeOptions([]);
                        if (newValue) {
                          const ts = new Date(newValue).getTime() / 1000;
                          fetchDates(
                            formikProps.values.departmentID,
                            formikProps.values.officer,
                            ts,
                            setTimeOptions
                          );
                        }
                        formikProps.setFieldValue("date", newValue);
                      }}
                      renderInput={(params) => (
                        <TextFieldWithCSS
                          {...params}
                          name="date"
                          error={
                            formikProps.touched.date &&
                            Boolean(formikProps.errors.date)
                          }
                        />
                      )}
                      shouldDisableDate={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return disabledDate.includes(date) || date < today;
                      }}
                      inputFormat="dd/MM/yyyy"
                    />
                  </FormControl>
                </LocalizationProvider>
              </Grid>
            )}

            {/* Time Slot */}
            {!premisesID && (
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
                  Select Time Slot <span style={{ color: "red" }}>*</span>
                </Typography>
                <SelectField
                  name="time"
                  // label={"Select Time Slot"}
                  placeHolder={"Select Time Slot"}
                  value={formikProps.values.time || []}
                  options={timeOptions.map((t) => ({
                    id: t.slot_id,
                    name: t.slot_time,
                  }))}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  error={
                    formikProps.touched.time && Boolean(formikProps.errors.time)
                  }
                  multiple
                  disabled={timeOptions.length === 0}
                  xs={12}
                />
              </Grid>
            )}

            {/* Purpose */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
                Purpose of visit <span style={{ color: "red" }}>*</span>
              </Typography>
              <SelectField
                name="PurposeVisit"
                placeHolder="Purpose Visit"
                value={formikProps.values.PurposeVisit}
                options={[
                  { id: "official", name: "Official" },
                  { id: "personal", name: "Personal" },
                  { id: "other", name: "Other" },
                ]}
                onChange={formikProps.handleChange}
                onBlur={formikProps.handleBlur}
                error={
                  formikProps.touched.PurposeVisit &&
                  Boolean(formikProps.errors.PurposeVisit)
                }
                xs={12}
              />
            </Grid>

            {/* Reason */}
            <Grid item xs={12}>
              <TextFieldWithCSS
                fullWidth
                placeholder="Please fill in the reason for your appointment"
                name="reason"
                value={formikProps.values.reason}
                onChange={(e) => {
                  if (e.target.value.length <= 250) {
                    formikProps.handleChange(e);
                  }
                }}
                onBlur={formikProps.handleBlur}
                error={
                  formikProps.touched.reason &&
                  Boolean(formikProps.errors.reason)
                }
                multiline
                rows={6}
                helperText={`${formikProps.values.reason.length} / 250 characters`}
              />
            </Grid>
          </>
        )}
      </Grid>

      {formikProps.values.subDepartment && (
        <>
          <MandatoryNote />
          <LoadingButton
            disabled={!formikProps.dirty || !formikProps.isValid}
            onClick={formikProps.handleSubmit}
          >
            Save & Proceed
          </LoadingButton>
        </>
      )}
    </Fragment>
  );
}

export default AppointmentFormFields;
